import React, { useContext } from 'react';

import styled from '@emotion/styled';

import useSearchCustomerInfo from '../../../../hooks/dashboard/driver-list/useSearchCustomerInfo';
import useSearchDriver from '../../../../hooks/dashboard/driver-list/useSearchDriver';
import useTripLog from '../../../../hooks/dashboard/trip-log/useTripLog';
import {
  TripLogInitialValues,
  TripLogInterface,
  TripLogSchema,
} from '../../../../models/dashboard/trip-log/trip-log';
import { createTripLogActions } from '../../../../redux/slices/createTripLogSlice';
import { useAppDisPatch, useAppSelector } from '../../../../redux/store';
import FormButtonArea from '../../../Button/FormButtonArea';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import DriverCard from '../../../Cards/DriverCard';
import CollapsibleContainer from '../../../Collapsible/CollapsibleContainer';
import { CompoundSearchSelect } from '../../../FormCom/CompoundSearchSelect/CompoundSearchSelect';
import CustomerInfoCard from '../../../FormCom/CompoundSearchSelect/CustomerInfoCard';
import CustomSelectSearchAsyncField from '../../../FormCom/CustomSelectSearchAsyncField';
import { FormComponent } from '../../../FormCom/FormComponent';
import { TextInputField } from '../../../FormCom/FormField';
import AddIcon from '../../../Icons/AddIcon';
import RemoveIcon from '../../../Icons/RemoveIcon';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';
import SaveToContact from './SaveToContact';

interface TripLogModalProps {
  onSuccess?: () => void;
}

const TripLogModal: React.FC<TripLogModalProps> = ({ onSuccess }) => {
  const modalContext = useContext(ModalContext);
  const { tripLog, loading } = useTripLog();
  const dispatch = useAppDisPatch();
  const { tripLogs } = useAppSelector((state) => state.tripLogData);
  const tripLogSize = tripLogs.length;
  const {
    searchDriver,
    loading: searchLoading,
    fetchedDriverData,
  } = useSearchDriver();

  const {
    searchCustomerInfo,
    loading: searchCustomerInfoLoading,
    fetchedCustomerInfoData,
  } = useSearchCustomerInfo();

  const onSubmit = () => {
    const payload: TripLogInterface[] = tripLogs.map((tripLogData) => ({
      driverId: tripLogData.driverId,
      dateOfTrip: tripLogData.dateOfTrip,
      customerEmail: tripLogData.customerEmail,
    }));

    tripLog(payload, () => {
      dispatch(createTripLogActions.reset());
      onSuccess?.();
    });
  };

  const handleTripLogFieldChange = (
    tripLogId: string,
    field: keyof TripLogInterface,
    value: string | string[],
  ) => {
    const currentTripLog = tripLogs.find((triplog) => triplog.id === tripLogId);

    if (!currentTripLog) {
      return;
    }

    dispatch(
      createTripLogActions.updateTripLogsAtId({
        id: tripLogId,
        tripLog: {
          driverId: currentTripLog.driverId,
          dateOfTrip: currentTripLog.dateOfTrip,
          customerEmail: currentTripLog.customerEmail,
          [field]: value,
        },
      }),
    );
  };

  const handleAddingTripLog = (values: TripLogInterface, tripLogId: string) => {
    dispatch(
      createTripLogActions.updateTripLogsAtId({
        id: tripLogId,
        tripLog: values,
      }),
    );
    dispatch(createTripLogActions.addEmptyTripLog());
  };

  const handleRemovingTripLog = (tripLogId: string) => {
    if (tripLogSize > 1) {
      dispatch(createTripLogActions.deleteTripLogsAtId(tripLogId));
    }
  };

  const getInitialValues = (
    tripLogData: TripLogInterface,
  ): TripLogInterface => {
    const isEmptyTripLog =
      !tripLogData.driverId &&
      !tripLogData.dateOfTrip &&
      !tripLogData.customerEmail;

    if (isEmptyTripLog) {
      return TripLogInitialValues;
    }

    return {
      driverId: tripLogData.driverId || '',
      dateOfTrip: tripLogData.dateOfTrip || '',
      customerEmail: tripLogData.customerEmail || '',
    };
  };

  const isSubmitDisabled =
    loading ||
    !tripLogs.every((tripLogData) => TripLogSchema.isValidSync(tripLogData));
  const today = new Date().toISOString().split('T')[0];

  return (
    <ModalContainer
      label='Create a Trip Log'
      description='Data stored within Logirate is protected against unauthorized access.'
      showSuccessComponent={modalContext?.showSuccessComponent}
    >
      <SaveToContact />

      {tripLogs.map((triplog, index) => (
        <FormComponent
          key={triplog.id}
          initialValues={getInitialValues(triplog)}
          schema={TripLogSchema}
          onSubmit={(values: TripLogInterface) =>
            handleAddingTripLog(values, triplog.id)
          }
          className={'small-gap'}
        >
          <CollapsibleContainer
            key={triplog.id}
            title={`Trip ${index + 1}`}
            open
          >
            <CustomSelectSearchAsyncField
              label='Select Driver'
              placeholder='Select Driver'
              name='driverId'
              id='driverId'
              onChange={(value) =>
                handleTripLogFieldChange(triplog.id, 'driverId', value)
              }
              renderComponent={(data) => <DriverCard data={data} />}
              fetchDataFn={searchDriver}
              fetchDataLoading={searchLoading}
              fetchedData={fetchedDriverData}
              requiredDataKey='id'
              presentationDataKey='name'
            />
            <TextInputField
              label='Date of Trip'
              name='dateOfTrip'
              id='dateOfTrip'
              type='date'
              max={today}
              placeholder='Date of Trip'
              onValueChange={(value) =>
                handleTripLogFieldChange(triplog.id, 'dateOfTrip', value)
              }
            />

            <CompoundSearchSelect
              label='Customer email(s)'
              name='customerEmail'
              id='customerEmail'
              placeholder='Enter email and press enter'
              onChange={(values) =>
                handleTripLogFieldChange(triplog.id, 'customerEmail', values)
              }
              renderComponent={(data) => <CustomerInfoCard data={data} />}
              fetchDataFn={searchCustomerInfo}
              fetchDataLoading={searchCustomerInfoLoading}
              fetchedData={fetchedCustomerInfoData}
              requiredDataKey='email'
              presentationDataKey='name'
              // showErrorLabel={false}
              allowValidEmail
            />

            {tripLogSize > 1 && (
              <>
                <TripBtn
                  className='delete'
                  btnType='button'
                  onClick={() => handleRemovingTripLog(triplog.id)}
                >
                  Remove Trip <RemoveIcon />
                </TripBtn>
              </>
            )}
          </CollapsibleContainer>

          {tripLogSize !== index + 1 && <HorizontalLine />}

          {tripLogSize === index + 1 && (
            <TripBtn>
              Add Another Trip <AddIcon />
            </TripBtn>
          )}
        </FormComponent>
      ))}

      <FormButtonArea>
        <FormSubmitButton
          className='clear'
          onClick={() => {
            dispatch(createTripLogActions.reset());
            modalContext?.setShowModal(false);
          }}
        >
          Cancel
        </FormSubmitButton>
        <FormSubmitButton
          loading={loading}
          disabled={isSubmitDisabled}
          btnType='button'
          onClick={onSubmit}
        >
          Submit
        </FormSubmitButton>
      </FormButtonArea>
    </ModalContainer>
  );
};

export default TripLogModal;

const HorizontalLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.palette.borderColor};
`;

const TripBtn = styled(FormSubmitButton)`
  display: flex;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.blueColor};
  width: fit-content;
  cursor: pointer;

  &.delete {
    color: #ff0500;
  }

  padding: 0px;
  border: none;
  border-radius: 0;
  background: none;
`;
